import {
  caseTransformingAxios,
  responseDataToCamelCase,
  requestDataToSnakeCase,
} from "@shared/v2/caseTransformingAxios";
import { useEffect, useMemo, useState } from "react";
import ConferenceTaskCard from "./ConferenceTaskCard";
import { Task } from "./types";
import { groupBy, sortBy } from "lodash";
import Typography from "@shared/v2/Typography";
import Button from "@shared/v2/Button";
import TaskForm from "../../../Tasks/components/TaskForm";

interface UseConferenceTasksResult {
  actions: {
    toggleComplete: (task: Task) => void;
    upsertTask: (task: Task) => void;
    deleteTask: (task: Task) => void;
  };
  error: string | null;
  loading: boolean;
  taskCounts: { [key in Task["dueness"]]?: Task[] };
  tasks: Task[];
}

const useConferenceTasks = ({
  personUuid,
  type,
}: {
  personUuid: string;
  type?: "Appointment";
}): UseConferenceTasksResult => {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [error, setError] = useState<string | null>(null);
  const taskCounts = useMemo(() => groupBy(tasks, "dueness"), [tasks]);

  useEffect(() => {
    if (!personUuid) return;
    caseTransformingAxios
      .get<{ tasks: Task[] }>(`/tasks/taskable_index?id=${personUuid}`)
      .then((res) =>
        setTasks(
          sortBy(
            res.data.tasks.filter(
              (task) =>
                task.dueness !== "completed" &&
                ((!type && task.type !== "Appointment") || task.type === type),
            ),
            ["dueOn"],
          ),
        ),
      )
      .catch(() => setError("Whoops! Something went wrong while fetching tasks."))
      .finally(() => setLoading(false));
  }, [personUuid]);

  return {
    actions: {
      toggleComplete: (task: Task) => {
        setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, loading: true } : t)));
        caseTransformingAxios
          .patch<{ task: Task }>(
            `/tasks/${task.id}/${task.dueness === "completed" ? "uncomplete" : "complete"}`,
          )
          .then((res) =>
            setTasks((prev) =>
              prev.map((t) =>
                t.id === task.id
                  ? {
                      ...t,
                      ...res.data.task,
                      loading: false,
                    }
                  : t,
              ),
            ),
          )
          .catch(() => {
            setError("Whoops! Something went wrong while completing the task.");
            setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, loading: false } : t)));
          });
      },
      upsertTask: (task: Task) => {
        setTasks((prev) => {
          const existingTaskIndex = prev.findIndex((t) => t.id === task.id);
          if (existingTaskIndex !== -1) {
            const updatedTasks = [...prev];
            updatedTasks[existingTaskIndex] = task;
            return updatedTasks;
          }
          return sortBy([...prev, task], ["dueOn"]);
        });
      },
      deleteTask: (task: Task) => {
        setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, loading: true } : t)));
        caseTransformingAxios
          .delete(`/tasks/${task.id}`)
          .then(() => {
            setTasks((prev) => prev.filter((t) => t.id !== task.id));
          })
          .catch(() => {
            setError("Whoops! Something went wrong while deleting the task.");
            setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, loading: false } : t)));
          });
      },
    },
    error,
    loading,
    taskCounts,
    tasks,
  };
};

const ConferenceTasks = ({ personUuid, type }: { personUuid: string; type?: "Appointment" }) => {
  const [editTask, setEditTask] = useState<Task | null>(null);
  const { actions, loading, tasks, taskCounts, error } = useConferenceTasks({ personUuid, type });
  const taskable = useMemo(() => ({ uuid: personUuid, taskable_type: "Person" }), [personUuid]);

  return (
    <div className="tw-flex-1 tw-flex tw-flex-col">
      {!editTask && (
        <>
          <div className="tw-flex tw-gap-[16px] tw-items-center tw-justify-between tw-mt-[16px] tw-px-[16px]">
            <div className="tw-flex tw-items-center tw-gap-[4px]">
              {/* @ts-expect-error props */}
              <Typography variant="subtitle-s" className="!tw-text-[#E5342E]">
                {taskCounts.overdue?.length || 0} Overdue
              </Typography>
              <span className="tw-text-gray-30">|</span>
              {/* @ts-expect-error props */}
              <Typography variant="subtitle-s" className="!tw-text-yellow-100">
                {taskCounts.due_today?.length || 0} Due Today
              </Typography>
              <span className="tw-text-gray-30">|</span>
              {/* @ts-expect-error props */}
              <Typography variant="subtitle-s">{taskCounts.upcoming?.length || 0} Upcoming</Typography>
            </div>
            {/* @ts-expect-error props */}
            <Button
              schema="secondary"
              onClick={() => {
                setEditTask({} as Task);
                window.clarity?.("event", `conferenceDialerAdd${type || "Task"}`);
              }}
            >
              + {type || "Task"}
            </Button>
          </div>
          <div className="tw-flex-1 tw-flex tw-flex-col tw-py-[16px] tw-gap-[8px] tw-overflow-auto tw-mb-[8px] tw-px-[16px]">
            {loading && "Loading..."}
            {error && error}
            {tasks.map((task) => (
              <ConferenceTaskCard
                key={task.id}
                task={task}
                onToggleComplete={actions.toggleComplete}
                onDelete={actions.deleteTask}
                onEdit={() => setEditTask(requestDataToSnakeCase(task) as Task)}
              />
            ))}
          </div>
        </>
      )}

      {editTask && (
        <TaskForm
          isCreatingAppointment={type === "Appointment"}
          isEmbedded
          onClose={() => setEditTask(null)}
          task={editTask}
          taskable={taskable}
          updateTask={(task) => actions.upsertTask(responseDataToCamelCase(task) as Task)}
        />
      )}
    </div>
  );
};

export default ConferenceTasks;
